import React, { useState, useEffect } from 'react';
import { Button, Badge } from 'react-bootstrap';
import { useTranslation } from "react-i18next";
import { useAuth } from "../../base";
import WhatsNewModal from './WhatsNewModal';
import './WhatsNewModal.css';

const WhatsNewButton = ({ className = "", showOnLogin = true, handleOpenContactForm }) => {
  const { t } = useTranslation();
  const currentUser = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [hasUnreadUpdates, setHasUnreadUpdates] = useState(false);
  const [shouldAutoShow, setShouldAutoShow] = useState(false);
  const isGuestUser = Boolean(currentUser?.email?.startsWith('guest_'));

  // This should match the updates in WhatsNewModal
  const latestVersion = "5.9.0"; // Update this when you add new features

  const getLastSeenVersion = () => {
    try {
      return localStorage.getItem('lastSeenNewsVersion') || '0.0.0';
    } catch (e) {
      return '0.0.0';
    }
  };

  const getLastLoginCheck = () => {
    try {
      return localStorage.getItem('lastNewsLoginCheck') || '0';
    } catch (e) {
      return '0';
    }
  };

  const setLastLoginCheck = () => {
    try {
      localStorage.setItem('lastNewsLoginCheck', Date.now().toString());
    } catch (e) {
      console.warn('Could not save last login check');
    }
  };

  const compareVersions = (a, b) => {
    const parseVersion = (version) => version.split('.').map(Number);
    const versionA = parseVersion(a);
    const versionB = parseVersion(b);
    
    for (let i = 0; i < Math.max(versionA.length, versionB.length); i++) {
      const numA = versionA[i] || 0;
      const numB = versionB[i] || 0;
      if (numA > numB) return 1;
      if (numA < numB) return -1;
    }
    return 0;
  };

  useEffect(() => {
    if (currentUser) {
      const lastSeenVersion = getLastSeenVersion();
      const hasNewUpdates = compareVersions(latestVersion, lastSeenVersion) > 0;
      setHasUnreadUpdates(hasNewUpdates);

      // Check if we should auto-show on login
      if (showOnLogin && hasNewUpdates && !isGuestUser) {
        const lastLoginCheck = getLastLoginCheck();
        const timeSinceLastCheck = Date.now() - parseInt(lastLoginCheck);
        const oneDay = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

        // Auto-show if it's been more than a day since last check or if never checked
        if (timeSinceLastCheck > oneDay || lastLoginCheck === '0') {
          setShouldAutoShow(true);
          setLastLoginCheck();
        }
      } else {
        setShouldAutoShow(false);
      }
    }
  }, [currentUser, latestVersion, showOnLogin, isGuestUser]);

  useEffect(() => {
    // Auto-show modal after a short delay to ensure UI is loaded
    if (shouldAutoShow && currentUser) {
      const timer = setTimeout(() => {
        setShowModal(true);
        setShouldAutoShow(false);
      }, 1500);

      return () => clearTimeout(timer);
    }
  }, [shouldAutoShow, currentUser]);

  const handleButtonClick = () => {
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setHasUnreadUpdates(false);
  };

  return (
    <>
      <Button
        variant="outline-danger"
        size="sm"
        onClick={handleButtonClick}
        className={`whats-new-button ${className}`}
        title={t("whatsNew.buttonTooltip", "See what's new in this version")}
      >
        <span className="news-notification-icon">📢</span>
        {t("whatsNew.buttonText", "What's New")}
        {hasUnreadUpdates && (
          <Badge bg="light" text="dark" className="ms-1" style={{ fontSize: '0.6rem', padding: '2px 4px' }}>
            NEW
          </Badge>
        )}
      </Button>

      {showModal && (
        <WhatsNewModal
          show={showModal}
          onHide={handleModalClose}
          showBadge={hasUnreadUpdates}
          handleOpenContactForm={handleOpenContactForm}
        />
      )}
    </>
  );
};

export default WhatsNewButton;
